/**
 * useSaveProject Hook
 *
 * Handles the complete project save workflow including:
 * - Thumbnail generation using canvas API
 * - Multi-floor data aggregation and payload preparation
 * - API mutations for creating/updating map data
 * - Project thumbnail updates
 * - Local storage synchronization
 * - Parent application messaging
 * - Error handling and success modals
 *
 * Extracted from EditorPage.tsx Phase 8 refactoring
 */

import { useState } from 'react'
import type { dia } from '@joint/core'
import { useFloorStore, type MapData as FloorMapData } from '../../../map-editor-shared/store/floorStore'
import { useProjectStore } from '../../../map-editor-shared/store/projectStore'
import { clearRadiusCircles } from '../../../map-editor-features/editor/lib/relationshipUtils'
import { useParentCommunication } from '../../../map-editor-shared/hooks/useParentCommunication'
import { getActiveOverlayCanvas } from './useCanvasEditorOverlay'

interface UseSaveProjectParams {
  graph: dia.Graph | null
  paper: dia.Paper | null
  currentFloor: string | null
  currentLot: string | null
  projectId: number | null
  floors: Floor[]
  hasMapData: boolean
  selectedElementId: string | null
  createMapDataMutation: any // TODO: Type this properly with React Query mutation type
  updateMapDataMutation: any
  updateProjectMutation: any
  onError?: (error: Error) => void
}

interface UseSaveProjectReturn {
  /** 저장 실행 — 성공 여부 반환(기존 호출부는 반환값 무시해도 무방). */
  handleSave: () => Promise<boolean>
  isSaving: boolean
  savedThumbnail: string | null
  savedMapId: string | null
  showSaveSuccessModal: boolean
  setShowSaveSuccessModal: (show: boolean) => void
}

interface Floor {
  id: string
  name: string
  order: number
  lotId: string
  mapData?: {
    graphJson?: any
    metadata?: any
    assets?: any[]
    objects?: any[]
    selectedElementId?: string
    csv?: unknown | null
    dxf?: unknown | null
  }
}

interface MapDataPayload {
  floors: FloorPayload[]
  template_id?: number
}

interface FloorPayload {
  name: string
  order: number
  graphJson: any
}

/**
 * Custom hook for managing project save operations
 *
 * @param params - Configuration parameters including graph, paper, current floor/lot, mutations
 * @returns Save handler function and related state
 */
export const useSaveProject = ({
  graph,
  paper,
  currentFloor,
  currentLot,
  projectId,
  floors,
  hasMapData,
  selectedElementId,
  createMapDataMutation,
  updateMapDataMutation,
  updateProjectMutation,
  onError,
}: UseSaveProjectParams): UseSaveProjectReturn => {
  const [isSaving, setIsSaving] = useState(false)
  const [savedThumbnail, setSavedThumbnail] = useState<string | null>(null)
  const [savedMapId, setSavedMapId] = useState<string | null>(null)
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false)

  // Zustand store actions
  const { updateFloor, updateFloorMapData } = useFloorStore()
  const { getLotById } = useProjectStore()
  const currentLotData = currentLot ? getLotById(currentLot) : undefined

  // Parent communication hook
  const { sendMessage } = useParentCommunication()

  const handleSave = async (): Promise<boolean> => {
    if (!graph || !currentFloor || !currentLot || !projectId) {
      return false
    }


    setIsSaving(true)
    try {
      // Step 1: Clear radius circles before saving
      clearRadiusCircles(graph)

      const json = graph.toJSON()
      const currentFloorData = floors.find(f => f.id === currentFloor)

      if (!currentFloorData) {
        setIsSaving(false)
        return false
      }

      // Step 2: Generate thumbnail and save as base64
      let thumbnailFileName: string | null = null

      // CANVAS_EDITOR 오버레이가 떠 있으면 paper SVG 는 얼려져 셀 뷰가 없어
      // SVG 캡처가 빈 썸네일이 된다 — 실제 화면인 오버레이 캔버스를 그대로
      // 축소 캡처한다 (뷰포트 그대로라는 기존 의도와 동일).
      const overlayCanvas = getActiveOverlayCanvas()
      if (overlayCanvas && overlayCanvas.width > 0 && overlayCanvas.height > 0) {
        try {
          const targetWidth = 800
          const targetHeight = Math.round((targetWidth * overlayCanvas.height) / overlayCanvas.width)
          const canvas = document.createElement('canvas')
          canvas.width = targetWidth
          canvas.height = targetHeight
          const ctx = canvas.getContext('2d')
          if (ctx) {
            const canvasBg = getComputedStyle(document.documentElement)
              .getPropertyValue('--color-canvas-bg')
              .trim() || '#0f172a'
            ctx.fillStyle = canvasBg
            ctx.fillRect(0, 0, targetWidth, targetHeight)
            ctx.drawImage(overlayCanvas, 0, 0, targetWidth, targetHeight)
            const base64Thumbnail = canvas.toDataURL('image/png', 0.9)
            thumbnailFileName = base64Thumbnail
            setSavedThumbnail(base64Thumbnail)
          }
        } catch (err) {
          // Continue with save even if thumbnail fails
          thumbnailFileName = null
          setSavedThumbnail(null)
        }
      } else if (paper && graph) {
        const contentBBox = graph.getBBox()
        // 사용자가 보고 있는 화면(뷰포트)을 그대로 캡처한다. 콘텐츠 전체
        // bbox 를 저해상도 캔버스에 욱여넣으면 수백 개 구역의 선이
        // 서브픽셀로 뭉개져 썸네일이 거의 보이지 않는다.
        // 주의: paper.getArea() 는 paper 전체(20000px 고정)를 반환하므로
        // 실제 화면에 보이는 영역은 클리핑 컨테이너 rect 로 계산해야 한다.
        const containerEl = paper.el.parentElement ?? paper.el
        const containerRect = containerEl.getBoundingClientRect()
        const clientVisible = {
          x: Math.max(containerRect.left, 0),
          y: Math.max(containerRect.top, 0),
          width: Math.min(containerRect.right, window.innerWidth) - Math.max(containerRect.left, 0),
          height: Math.min(containerRect.bottom, window.innerHeight) - Math.max(containerRect.top, 0)
        }
        const visibleArea = clientVisible.width > 0 && clientVisible.height > 0
          ? paper.clientToLocalRect(clientVisible)
          : null

        if (
          contentBBox && contentBBox.width > 0 && contentBBox.height > 0 &&
          visibleArea && visibleArea.width > 0 && visibleArea.height > 0
        ) {
          const contentArea = {
            x: visibleArea.x,
            y: visibleArea.y,
            width: visibleArea.width,
            height: visibleArea.height
          }

          const canvas = document.createElement('canvas')
          // 뷰포트 비율을 유지한 고해상도 캡처 → 카드(object-fit: contain)에
          // 좌우 띠 없이 화면과 동일하게 보인다.
          const targetWidth = 800
          const targetHeight = Math.round((targetWidth * contentArea.height) / contentArea.width)
          canvas.width = targetWidth
          canvas.height = targetHeight
          const ctx = canvas.getContext('2d')

          if (ctx) {
            // 에디터 캔버스와 동일한 배경색 사용 (테마 추종)
            const canvasBg = getComputedStyle(document.documentElement)
              .getPropertyValue('--color-canvas-bg')
              .trim() || '#0f172a'
            ctx.fillStyle = canvasBg
            ctx.fillRect(0, 0, targetWidth, targetHeight)

            const scaleX = targetWidth / contentArea.width
            const scaleY = targetHeight / contentArea.height
            const scale = Math.min(scaleX, scaleY)

            const scaledWidth = contentArea.width * scale
            const scaledHeight = contentArea.height * scale
            const offsetX = (targetWidth - scaledWidth) / 2
            const offsetY = (targetHeight - scaledHeight) / 2

            const svgClone = paper.svg.cloneNode(true) as SVGSVGElement
            // 레이어 그룹에 남은 현재 줌/팬 transform 을 제거해 로컬 좌표계로
            // 통일한다. 남겨두면 로컬 좌표 viewBox 와 이중 적용되어 콘텐츠가
            // viewBox 밖으로 밀려나 빈 썸네일이 된다.
            svgClone.querySelector('.joint-layers')?.removeAttribute('transform')

            // 도형 색은 var(--color-map-*) CSS 변수인데, SVG 를 단독 blob 으로
            // 직렬화하면 변수 정의가 사라져 fill 이 검정으로 떨어진다.
            // 원본에서 계산된 실제 색을 클론에 인라인으로 고정한다.
            const originalNodes = paper.svg.querySelectorAll<SVGElement>('*')
            const cloneNodes = svgClone.querySelectorAll<SVGElement>('*')
            originalNodes.forEach((orig, i) => {
              const cloneNode = cloneNodes[i]
              if (!cloneNode) return
              for (const attr of ['fill', 'stroke'] as const) {
                const value = orig.getAttribute(attr)
                if (value && value.includes('var(')) {
                  cloneNode.setAttribute(attr, getComputedStyle(orig).getPropertyValue(attr))
                }
              }
            })
            svgClone.setAttribute('viewBox', `${contentArea.x} ${contentArea.y} ${contentArea.width} ${contentArea.height}`)
            svgClone.setAttribute('width', String(contentArea.width))
            svgClone.setAttribute('height', String(contentArea.height))

            const svgData = new XMLSerializer().serializeToString(svgClone)
            const img = new Image()
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
            const url = URL.createObjectURL(svgBlob)

            try {
              await new Promise<void>((resolve, reject) => {
                img.onload = () => {
                  try {
                    ctx.save()
                    ctx.translate(offsetX, offsetY)
                    ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight)
                    ctx.restore()

                    // Convert canvas to base64
                    const base64Thumbnail = canvas.toDataURL('image/png', 0.9)
                    thumbnailFileName = base64Thumbnail
                    setSavedThumbnail(base64Thumbnail)

                    URL.revokeObjectURL(url)
                    resolve()
                  } catch (err) {
                    reject(err)
                  }
                }
                img.onerror = (err) => {
                  URL.revokeObjectURL(url)
                  reject(new Error('Failed to load SVG image'))
                }
                img.src = url
              })
            } catch (err) {
              // Continue with save even if thumbnail fails
              thumbnailFileName = null
              setSavedThumbnail(null)
            }
          }
        } else {
        }
      } else {
      }

      // Step 3: Save current floor data to local storage first
      updateFloorMapData(currentFloor, {
        graphJson: json,
      })

      // Step 4: Prepare ALL floors data for API
      // 빈 graphJson 도 메타데이터(name/order)는 보존하여 라운드트립 시 사용자 입력
      // floor 이름이 살아있도록 한다. 빈 floor 은 { cells: [] } 로 채워 보냄.
      const projectFloors = floors.filter(f => f.lotId === currentLot)
      const floorsPayload: FloorPayload[] = projectFloors.map(floor => {
        // Use current graph for active floor, saved data for others
        const floorGraphJson = floor.id === currentFloor
          ? json
          : floor.mapData?.graphJson

        return {
          name: floor.name,
          order: floor.order,
          graphJson: floorGraphJson ?? { cells: [] },
        }
      })

      const mapDataPayload: MapDataPayload = {
        floors: floorsPayload,
        template_id: currentLotData?.templateId // Already a number
      }


      // Step 5: Save or update map data via API
      console.log(`[useSaveProject] Saving`, {
        projectId,
        hasMapData,
        floorCount: floorsPayload.length,
        floors: floorsPayload.map(f => ({
          name: f.name, order: f.order,
          graphJsonCells: f.graphJson?.cells?.length ?? 0,
        })),
        payloadSize: JSON.stringify(mapDataPayload).length,
      });
      if (hasMapData) {
        // Update existing map data
        await updateMapDataMutation.mutateAsync({
          projectId,
          mapData: mapDataPayload
        })
      } else {
        // Create new map data
        await createMapDataMutation.mutateAsync({
          projectId,
          mapData: mapDataPayload,
          templateId: mapDataPayload.template_id
        })
      }

      // Step 6: Update project thumbnail if generated
      if (thumbnailFileName) {
        await updateProjectMutation.mutateAsync({
          id: projectId,
          data: {
            thumbnail: thumbnailFileName
          }
        })
      }

      // Step 7: Update local storage for offline support
      const prevMd = currentFloorData.mapData as FloorMapData | undefined
      const nextMapData: FloorMapData = {
        ...(prevMd ?? { csv: null, dxf: null, metadata: {}, assets: [], objects: [] }),
        graphJson: json,
        metadata: prevMd?.metadata ?? {},
        assets: prevMd?.assets ?? [],
        objects: prevMd?.objects ?? [],
        csv: prevMd?.csv ?? null,
        dxf: prevMd?.dxf ?? null,
        selectedElementId: selectedElementId ?? undefined,
      }
      updateFloor(currentFloor, {
        mapData: nextMapData,
      })

      setIsSaving(false)
      setSavedMapId(currentLot)
      setShowSaveSuccessModal(true)

      // Step 8: Notify parent application of save success
      sendMessage({
        type: 'SAVE_SUCCESS',
        projectId: currentLot,
        data: { floors: floorsPayload }
      })

      return true
    } catch (error) {
      setIsSaving(false)
      // Call error handler if provided
      if (onError) {
        onError(error instanceof Error ? error : new Error('Failed to save project'))
      }

      // Notify parent application of save error
      sendMessage({
        type: 'SAVE_ERROR',
        error: error instanceof Error ? error.message : 'Failed to save project'
      })
      return false
    }
  }

  return {
    handleSave,
    isSaving,
    savedThumbnail,
    savedMapId,
    showSaveSuccessModal,
    setShowSaveSuccessModal,
  }
}
